import { useState } from "react";
import { supabase } from "../supabase";
import { AGE_GROUPS, DIVISIONS, LEVELS } from "../data/teams";

export default function ManagerForm({ onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    // Manager details
    name: "", email: "", phone: "", faNumber: "",
    // Club & listing details
    club: "", badgeUrl: "", league: "", division: "Division 1", day: "Saturday",
    ageGroup: "U9", region: "", town: "",
    level: "Developing", levelDesc: "", positions: "", contact: "", 
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: "100%", background: "#1f2937", border: "1px solid #374151",
    borderRadius: 8, padding: "10px 12px", color: "#f3f4f6", fontSize: 13,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit"
  };
  const labelStyle = { color: "#6b7280", fontSize: 11, display: "block", marginBottom: 5, letterSpacing: 1 };

  const submitListing = async () => {
    setLoading(true);
    // Save manager registration
    await supabase.from("managers").insert([{
      name: form.name, email: form.email, phone: form.phone,
      club: form.club, fa_number: form.faNumber,
      town: form.town, region: form.region, status: "pending"
    }]);
    // Save listing
    await supabase.from("listings").insert([{
      club: form.club, league: form.league, division: form.division,
      day: form.day, age_group: form.ageGroup, region: form.region,
      town: form.town, level: form.level, level_desc: form.levelDesc,
      positions: form.positions.split(",").map(p => p.trim()).filter(Boolean),
      contact_email: form.email, phone: form.phone,
      badge_url: form.badgeUrl || null, status: "pending"
    }]);
    // Send confirmation email
    await supabase.functions.invoke("send-email", {
      body: { type: "manager", data: { name: form.name, email: form.email, club: form.club, town: form.town } }
    });
    setLoading(false);
    setStep(4);
  };

  if (step === 4) return (
    <div style={{ textAlign: "center", padding: 20 }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
      <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>You're on Grassrooting!</div>
      <div style={{ color: "#9ca3af", fontSize: 14, marginBottom: 20, lineHeight: 1.7 }}>
        Your listing for <strong style={{ color: "#f3f4f6" }}>{form.club}</strong> has been received.<br/>
        We'll review and publish it shortly — usually within a day.<br/>
        You'll receive a confirmation email at <strong style={{ color: "#f3f4f6" }}>{form.email}</strong>.
      </div>
      <button onClick={onClose} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>DONE</button>
    </div>
  );

  return (
    <div>
      {/* Progress bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[1,2,3].map(s => (
          <div key={s} style={{ flex: 1, height: 4, borderRadius: 4, background: step >= s ? "#10b981" : "#1f2937", transition: "background 0.3s" }} />
        ))}
      </div>
      <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 16, letterSpacing: 1 }}>
        STEP {step} OF 3 — {step===1 ? "YOUR DETAILS" : step===2 ? "YOUR TEAM" : "LISTING DETAILS"}
      </div>

      {/* ── STEP 1 — Manager Details ── */}
      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>YOUR NAME</label>
              <input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Full name" />
            </div>
            <div>
              <label style={labelStyle}>EMAIL ADDRESS</label>
              <input style={inputStyle} value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@email.com" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>PHONE (optional)</label>
            <input style={inputStyle} value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="07700 900000" />
          </div>
          <div>
            <label style={labelStyle}>FA AFFILIATION NUMBER <span style={{ color: "#ef4444" }}>*</span></label>
            <input style={inputStyle} value={form.faNumber} onChange={e => set("faNumber", e.target.value)} placeholder="e.g. 12345678" />
            <div style={{ color: "#4b5563", fontSize: 11, marginTop: 4 }}>Found on the FA Full-Time website or via your County FA.</div>
          </div>
          <button onClick={() => setStep(2)} disabled={!form.name || !form.email || !form.faNumber}
            style={{ background: form.name && form.email && form.faNumber ? "#10b981" : "#1f2937", color: form.name && form.email && form.faNumber ? "#fff" : "#4b5563", border: "none", borderRadius: 10, padding: "12px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: form.name && form.email && form.faNumber ? "pointer" : "not-allowed", letterSpacing: 1 }}>
            NEXT →
          </button>
        </div>
      )}

      {/* ── STEP 2 — Club Details ── */}
      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>CLUB NAME</label>
            <input style={inputStyle} value={form.club} onChange={e => set("club", e.target.value)} placeholder="e.g. South Wonston Swifts" />
          </div>
          <div>
            <label style={labelStyle}>CLUB BADGE URL (optional)</label>
            <input style={inputStyle} value={form.badgeUrl} onChange={e => set("badgeUrl", e.target.value)} placeholder="e.g. https://yourclub.com/badge.png" />
            <div style={{ color: "#4b5563", fontSize: 11, marginTop: 4 }}>Paste a link to your club badge — shown on your listing. Leave blank for default ⚽</div>
            {form.badgeUrl ? (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                <img src={form.badgeUrl} alt="Badge preview" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid #374151", background: "#1f2937" }} onError={e => e.target.style.display="none"} />
                <span style={{ color: "#6b7280", fontSize: 11 }}>Preview</span>
              </div>
            ) : null}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>TOWN / CITY</label>
              <input style={inputStyle} value={form.town} onChange={e => set("town", e.target.value)} placeholder="e.g. Andover" />
            </div>
            <div>
              <label style={labelStyle}>REGION / COUNTY</label>
              <input style={inputStyle} value={form.region} onChange={e => set("region", e.target.value)} placeholder="e.g. Hampshire" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>LEAGUE</label>
              <input style={inputStyle} value={form.league} onChange={e => set("league", e.target.value)} placeholder="e.g. Testway Youth League" />
            </div>
            <div>
              <label style={labelStyle}>DIVISION</label>
              <select style={inputStyle} value={form.division} onChange={e => set("division", e.target.value)}>
                {DIVISIONS.filter(d => d !== "All").map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>DAY</label>
              <select style={inputStyle} value={form.day} onChange={e => set("day", e.target.value)}>
                <option>Saturday</option><option>Sunday</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>AGE GROUP</label>
              <select style={inputStyle} value={form.ageGroup} onChange={e => set("ageGroup", e.target.value)}>
                {AGE_GROUPS.filter(a => a !== "All").map(a => <option key={a}>{a}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(1)} style={{ flex: 1, background: "#1f2937", color: "#9ca3af", border: "none", borderRadius: 10, padding: "12px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>← BACK</button>
            <button onClick={() => setStep(3)} disabled={!form.club || !form.town}
              style={{ flex: 2, background: form.club && form.town ? "#10b981" : "#1f2937", color: form.club && form.town ? "#fff" : "#4b5563", border: "none", borderRadius: 10, padding: "12px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: form.club && form.town ? "pointer" : "not-allowed", letterSpacing: 1 }}>
              NEXT →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3 — Listing Details ── */}
      {step === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>ABILITY LEVEL</label>
            <select style={inputStyle} value={form.level} onChange={e => set("level", e.target.value)}>
              {LEVELS.filter(l => l !== "All").map(l => <option key={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>DESCRIBE YOUR TEAM</label>
            <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} value={form.levelDesc} onChange={e => set("levelDesc", e.target.value)} placeholder="Tell parents and players what your team is like, training times, philosophy etc." />
          </div>
          <div>
            <label style={labelStyle}>POSITIONS NEEDED (comma separated)</label>
            <input style={inputStyle} value={form.positions} onChange={e => set("positions", e.target.value)} placeholder="e.g. Goalkeeper, Striker, Left Back" />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(2)} style={{ flex: 1, background: "#1f2937", color: "#9ca3af", border: "none", borderRadius: 10, padding: "12px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>← BACK</button>
            <button onClick={submitListing} disabled={loading}
              style={{ flex: 2, background: loading ? "#374151" : "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "12px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", letterSpacing: 1 }}>
              {loading ? "SUBMITTING..." : "POST LISTING ⚽"}
            </button>
          </div>
          <div style={{ color: "#4b5563", fontSize: 11, textAlign: "center" }}>Listings are reviewed and go live within 24 hours</div>
        </div>
      )}
    </div>
  );
}
